import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 아직 안 끝난 휴직 — 서버 {@code AttendanceBoardDtos.AbsenceMark} (사용자 지시 2026-10-26).
 *
 * <p>{@code type} 은 인사총무팀에게만 온다. 나머지에게는 {@code null} 이고 「휴직」 으로만 선다.
 */
export type AbsenceMark = {
  personnelId: string;
  type: 'CHILDCARE' | 'SICK' | 'OTHER' | null;
  startDate: string;
  endDate: string;
};

export type AbsenceBadgeInfo = { label: string; upcoming: boolean; startDate: string; endDate: string };

const TYPE_LABELS: Record<string, string> = { CHILDCARE: '육아휴직', SICK: '병가휴직', OTHER: '휴직' };

/**
 * 사람마다 배지 하나. 진행 중인 휴직이 예정보다 먼저고, 예정끼리는 가장 이른 것.
 *
 * <p>서버가 재시작 전이면 이 경로가 없다 — 실패는 빈 표로 읽어 이름 옆이 비어 있을 뿐 화면은 산다.
 */
export function useAbsenceMarks(): Map<string, AbsenceBadgeInfo> {
  const { data } = useQuery({
    queryKey: queryKeys.directoryAbsences,
    queryFn: () => apiFetch<AbsenceMark[]>('/v1/directory/absences'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return useMemo(() => {
    const today = localToday();
    const byPerson = new Map<string, AbsenceBadgeInfo>();
    for (const mark of data ?? []) {
      const upcoming = mark.startDate > today;
      const current = byPerson.get(mark.personnelId);
      if (current && (!current.upcoming || (upcoming && current.startDate <= mark.startDate))) continue;
      const label = TYPE_LABELS[mark.type ?? 'OTHER'] ?? '휴직';
      byPerson.set(mark.personnelId, {
        label: upcoming ? `${label} 예정` : label,
        upcoming,
        startDate: mark.startDate,
        endDate: mark.endDate,
      });
    }
    return byPerson;
  }, [data]);
}

/** 오늘(현지). ISO 로 자르면 UTC 라 한국 아침에 어제가 된다. */
function localToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
